/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/react-native
 * @description Renders a native provider selection and credential form for an
 * adapter domain.
 */

import type {
  AdapterDomain,
  DomainAdapterConfig,
  ProviderMeta,
} from "@geenius/adapters";
import {
  DOMAIN_LABELS,
  getProvidersForDomain,
  LAUNCH_DB_PROVIDERS,
} from "@geenius/adapters";
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { semanticColors } from "../bridges/TokenBridge";
import { Pressable, StyleSheet, Text, TextInput, View } from "../lib/native";

/**
 * Props for the native adapter configuration form.
 *
 * @property domain Adapter domain being configured.
 * @property initialConfig Optional provider configuration used to hydrate inputs.
 * @property onCancel Optional callback fired when the user dismisses the form.
 * @property onSave Callback receiving the validated provider configuration.
 * @property style Optional native style merged onto the form container.
 * @property testID Optional deterministic selector for tests and Storybook.
 * @property titleId Optional title identifier retained for parity with web forms.
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onCancel?: () => void;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  titleId?: string;
}

const SELECTABLE_DB_PROVIDER_IDS = new Set<string>([
  "localStorage",
  ...LAUNCH_DB_PROVIDERS,
]);

interface ProviderOptionField {
  key: string;
  label: string;
  description: string;
  placeholder?: string;
  keyboardType?: "default" | "numeric";
}

const PROVIDER_OPTION_FIELDS: Partial<
  Record<AdapterDomain, Record<string, ProviderOptionField[]>>
> = {
  db: {
    convex: [
      {
        key: "deployment",
        label: "Deployment",
        description: "Convex deployment or project slug.",
        placeholder: "team:project",
      },
    ],
    neon: [
      {
        key: "database",
        label: "Database",
        description: "Database name or branch label for downstream wiring.",
        placeholder: "main",
      },
    ],
    cloudflareKV: [
      {
        key: "namespace",
        label: "KV Namespace",
        description: "Worker binding name used by the downstream KV adapter.",
        placeholder: "ADAPTERS_KV",
      },
      {
        key: "defaultTtlSeconds",
        label: "Default TTL Seconds",
        description: "Optional TTL for cache/session style records.",
        placeholder: "300",
        keyboardType: "numeric",
      },
    ],
    memory: [
      {
        key: "seed",
        label: "Fixture Seed",
        description: "Optional deterministic seed for local test data.",
        placeholder: "demo",
      },
    ],
  },
};

function getSelectableProviders(domain: AdapterDomain): ProviderMeta[] {
  const providers = getProvidersForDomain(domain);
  if (domain !== "db") {
    return providers;
  }

  return providers.filter((entry) => SELECTABLE_DB_PROVIDER_IDS.has(entry.id));
}

function getOptionFields(
  domain: AdapterDomain,
  provider: string,
): ProviderOptionField[] {
  return PROVIDER_OPTION_FIELDS[domain]?.[provider] ?? [];
}

function stringifyOptionValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
}

function coerceOptionValue(field: ProviderOptionField, value: string): unknown {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }
  if (field.keyboardType !== "numeric") {
    return trimmed;
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : trimmed;
}

function validateOptionValues(
  domain: AdapterDomain,
  provider: string,
  optionValues: Record<string, string>,
): string | null {
  if (domain === "db" && provider === "cloudflareKV") {
    if (!optionValues.namespace?.trim()) {
      return "KV namespace is required";
    }

    const ttlValue = optionValues.defaultTtlSeconds?.trim();
    if (ttlValue) {
      const ttlSeconds = Number(ttlValue);
      if (!Number.isInteger(ttlSeconds) || ttlSeconds <= 0) {
        return "Default TTL seconds must be a positive integer";
      }
    }
  }

  for (const field of getOptionFields(domain, provider)) {
    if (field.keyboardType !== "numeric") {
      continue;
    }
    const raw = optionValues[field.key]?.trim();
    if (raw && !Number.isFinite(Number(raw))) {
      return `${field.label} must be a number`;
    }
  }

  return null;
}

/**
 * Renders the native provider picker and credential form for one domain.
 *
 * @param props Configuration form props including domain and save callback.
 * @returns A React Native form surface for provider selection.
 * @example
 * ```tsx
 * <AdapterConfigForm domain="auth" onSave={(config) => save(config)} />
 * ```
 */
export function AdapterConfigForm({
  domain,
  initialConfig,
  onSave,
  onCancel,
  style,
  testID,
}: AdapterConfigFormProps): JSX.Element {
  const providers = useMemo(() => getSelectableProviders(domain), [domain]);
  const initialProvider =
    providers.find(
      (entry: ProviderMeta) => entry.id === initialConfig?.provider,
    )?.id ??
    providers[0]?.id ??
    "";
  const [provider, setProvider] = useState(initialProvider);
  const [apiKey, setApiKey] = useState(initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = useState(initialConfig?.baseUrl ?? "");
  const [optionValues, setOptionValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(initialConfig?.options ?? {}).map(([key, value]) => [
        key,
        stringifyOptionValue(value),
      ]),
    ),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedMeta = providers.find(
    (entry: ProviderMeta) => entry.id === provider,
  );
  const needsApiKey = selectedMeta?.requiresApiKey ?? false;
  const needsBaseUrl = selectedMeta?.requiresBaseUrl ?? false;
  const optionFields = getOptionFields(domain, provider);

  useEffect(() => {
    const candidate = initialConfig?.provider;
    const nextProvider =
      providers.find((entry: ProviderMeta) => entry.id === candidate)?.id ??
      providers[0]?.id ??
      "";
    setProvider(nextProvider);
    setApiKey(initialConfig?.apiKey ?? "");
    setBaseUrl(initialConfig?.baseUrl ?? "");
    setOptionValues(
      Object.fromEntries(
        Object.entries(initialConfig?.options ?? {}).map(([key, value]) => [
          key,
          stringifyOptionValue(value),
        ]),
      ),
    );
    setError(null);
  }, [providers, initialConfig]);

  useEffect(() => {
    if (!needsApiKey && apiKey) {
      setApiKey("");
    }
    if (!needsBaseUrl && baseUrl) {
      setBaseUrl("");
    }
  }, [apiKey, baseUrl, needsApiKey, needsBaseUrl]);

  const handleProviderChange = (nextProvider: string): void => {
    if (nextProvider === provider) {
      return;
    }
    setProvider(nextProvider);
    // Reset provider-specific state so credentials/base URLs/options from the
    // previously selected provider cannot leak into the new submission.
    setApiKey("");
    setBaseUrl("");
    setOptionValues({});
    setError(null);
  };

  const handleSave = async (): Promise<void> => {
    setError(null);

    if (!provider) {
      setError("Select a provider");
      return;
    }

    if (!providers.some((entry: ProviderMeta) => entry.id === provider)) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey && !apiKey.trim()) {
      setError("API key is required");
      return;
    }

    if (needsBaseUrl && !baseUrl.trim()) {
      setError("Base URL is required");
      return;
    }

    const optionError = validateOptionValues(domain, provider, optionValues);
    if (optionError) {
      setError(optionError);
      return;
    }

    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider };
      if (needsApiKey && apiKey.trim()) {
        config.apiKey = apiKey.trim();
      }
      if (needsBaseUrl && baseUrl.trim()) {
        config.baseUrl = baseUrl.trim();
      }
      const options = Object.fromEntries(
        optionFields
          .map((field) => [
            field.key,
            coerceOptionValue(field, optionValues[field.key] ?? ""),
          ])
          .filter(([, value]) => value !== ""),
      );
      if (Object.keys(options).length > 0) {
        config.options = options;
      }
      await onSave(config);
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View style={[styles.form, style]} testID={testID ?? "adapter-config-form"}>
      <View>
        <Text accessibilityRole="header" style={styles.title}>
          Configure {DOMAIN_LABELS[domain]}
        </Text>
        <Text style={styles.subtitle}>
          Select and configure your preferred provider.
        </Text>
      </View>

      {error && (
        <View accessibilityRole="alert" style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Provider</Text>
        <View style={styles.providerList}>
          {providers.map((entry: ProviderMeta) => {
            const selected = provider === entry.id;
            return (
              <Pressable
                accessibilityLabel={`Select ${entry.name}`}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                key={entry.id}
                onPress={() => handleProviderChange(entry.id)}
                style={[styles.providerOption, selected && styles.selected]}
                testID={`provider-option-${entry.id}`}
              >
                <Text style={styles.providerName}>{entry.name}</Text>
                <Text style={styles.providerDescription}>
                  {entry.description}
                </Text>
                <Text style={styles.tier}>{entry.tier}</Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {needsApiKey && (
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>API Key</Text>
          <TextInput
            accessibilityLabel="API Key"
            autoCapitalize="none"
            onChangeText={setApiKey}
            placeholder={`Enter ${selectedMeta?.name ?? provider} API key`}
            secureTextEntry
            style={styles.input}
            testID="adapter-api-key"
            value={apiKey}
          />
        </View>
      )}

      {needsBaseUrl && (
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Base URL</Text>
          <TextInput
            accessibilityLabel="Base URL"
            autoCapitalize="none"
            onChangeText={setBaseUrl}
            placeholder="https://..."
            style={styles.input}
            testID="adapter-base-url"
            value={baseUrl}
          />
        </View>
      )}

      {optionFields.length > 0 && (
        <View style={styles.optionGrid}>
          {optionFields.map((field) => (
            <View key={field.key} style={styles.section}>
              <Text style={styles.sectionLabel}>{field.label}</Text>
              <TextInput
                accessibilityLabel={field.label}
                autoCapitalize="none"
                keyboardType={field.keyboardType}
                onChangeText={(value: string) =>
                  setOptionValues((current) => ({
                    ...current,
                    [field.key]: value,
                  }))
                }
                placeholder={field.placeholder}
                style={styles.input}
                testID={`adapter-option-${field.key}`}
                value={optionValues[field.key] ?? ""}
              />
              <Text style={styles.optionDescription}>{field.description}</Text>
            </View>
          ))}
        </View>
      )}

      <View style={styles.actions}>
        {onCancel && (
          <Pressable
            accessibilityLabel="Cancel adapter configuration"
            accessibilityRole="button"
            onPress={onCancel}
            style={styles.secondaryButton}
            testID="adapter-config-cancel"
          >
            <Text style={styles.secondaryButtonText}>Cancel</Text>
          </Pressable>
        )}
        <Pressable
          accessibilityLabel="Save adapter configuration"
          accessibilityRole="button"
          accessibilityState={{ disabled: isSaving || !provider }}
          disabled={isSaving || !provider}
          onPress={handleSave}
          style={[
            styles.primaryButton,
            (isSaving || !provider) && styles.disabledButton,
          ]}
          testID="adapter-config-save"
        >
          <Text style={styles.primaryButtonText}>
            {isSaving ? "Saving..." : "Save Configuration"}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  actions: {
    flexDirection: "row",
    gap: 10,
    justifyContent: "flex-end",
  },
  disabledButton: {
    opacity: 0.55,
  },
  errorBox: {
    backgroundColor: semanticColors.status.error.bg,
    borderColor: semanticColors.status.error.border,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
  },
  errorText: {
    color: semanticColors.status.error.text,
    fontSize: 13,
    fontWeight: "600",
  },
  form: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: 18,
    padding: 18,
  },
  input: {
    backgroundColor: semanticColors.surface.card,
    borderColor: semanticColors.surface.borderStrong,
    borderRadius: 8,
    borderWidth: 1,
    color: semanticColors.surface.text,
    fontSize: 15,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  optionDescription: {
    color: semanticColors.surface.textMuted,
    fontSize: 12,
    lineHeight: 17,
  },
  optionGrid: {
    gap: 12,
  },
  primaryButton: {
    backgroundColor: semanticColors.brand.bg,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 11,
  },
  primaryButtonText: {
    color: semanticColors.brand.onBrand,
    fontSize: 14,
    fontWeight: "800",
  },
  providerDescription: {
    color: semanticColors.surface.textMuted,
    fontSize: 12,
    lineHeight: 17,
    marginTop: 4,
  },
  providerList: {
    gap: 8,
  },
  providerName: {
    color: semanticColors.surface.text,
    fontSize: 14,
    fontWeight: "800",
  },
  providerOption: {
    backgroundColor: semanticColors.surface.bg,
    borderColor: semanticColors.surface.border,
    borderRadius: 10,
    borderWidth: 1,
    padding: 14,
  },
  secondaryButton: {
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  secondaryButtonText: {
    color: semanticColors.surface.textSoft,
    fontSize: 14,
    fontWeight: "700",
  },
  section: {
    gap: 8,
  },
  sectionLabel: {
    color: semanticColors.surface.textMuted,
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  selected: {
    backgroundColor: semanticColors.brand.bgSoft,
    borderColor: semanticColors.brand.border,
  },
  subtitle: {
    color: semanticColors.surface.textMuted,
    fontSize: 13,
    marginTop: 4,
  },
  tier: {
    alignSelf: "flex-start",
    backgroundColor: semanticColors.status.initializing.bg,
    borderRadius: 6,
    color: semanticColors.status.initializing.text,
    fontSize: 10,
    fontWeight: "800",
    marginTop: 8,
    overflow: "hidden",
    paddingHorizontal: 8,
    paddingVertical: 3,
    textTransform: "uppercase",
  },
  title: {
    color: semanticColors.surface.text,
    fontSize: 19,
    fontWeight: "900",
  },
});
